import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { safeAuthFetch } from '../../utils/safeFetch';
import { API_BASE_URL } from '../../config/api';
import { posthog } from '../../main';
import { fetchDiscoveryContexts } from '../../lib/discovery/discoveryApi';
import { startGuestRun } from '../../lib/discovery/startGuestRun';
import { guestStartErrorMessage } from '../../lib/discovery/guestStartErrorMessage';
import { useDiscoveryOrigin } from '../../lib/discovery/discoveryOrigin';
import ContextShelf from '../../components/guestDiscovery/ContextShelf';
import StartHereSlot from '../../components/guestDiscovery/StartHereSlot';
import ChooserEntry from '../../components/guestDiscovery/ChooserEntry';
import CommitmentSheetOverlay from '../../components/guestDiscovery/CommitmentSheetOverlay';
import AuthoringDemoDialog from '../../components/LazyAuthoringDemoDialog'; // #4231: dialog chunk fetched on first open
import { useSystemThemeWhenUnset } from '../../lib/theme/useSystemThemeWhenUnset';
import './guestDiscoveryShared.css';
import './GuestDiscoveryContextsPage.css';

/**
 * GuestDiscoveryContextsPage — J2 context picker (#1921, copy round 2 #2098,
 * chooser #1922).
 *
 * Route: `/discover`. Public — no auth required to VIEW (guest catalogue),
 * matching `/api/demo/discovery/contexts`'s own no-auth posture; a guest
 * session is only minted when the visitor actually starts a run (start-here
 * card → `startGuestRun`, same deferred-login pattern `HomePageV2.jsx`
 * already uses).
 *
 * "We can help you choose" navigates to `/discover/choose` — the real
 * chooser (#1922), a 3-question navigator that parameterises this same
 * shelf/shortlist presentation layer, never a second recommender (#1768).
 *
 * #2098 call 5: the "Browse all" footer link is DELETED (measured
 * 2026-08-08: zero scenarios reachable only through it, with the seven
 * chip counts summing to exactly the browse-all count). The API still
 * returns `browse_all_count` but this page no longer reads or renders it.
 */
function GuestDiscoveryContextsPage() {
  useSystemThemeWhenUnset(); // #3756: match the marketing site's OS dark mode
  const navigate = useNavigate();
  const origin = useDiscoveryOrigin();
  const { t } = useTranslation();
  const { isAuthenticated, user, loginAsGuest } = useAuth();

  const [contexts, setContexts] = useState([]);
  const [startHere, setStartHere] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [committingScenario, setCommittingScenario] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const [isAuthoringOpen, setIsAuthoringOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchDiscoveryContexts()
      .then((data) => {
        if (cancelled) return;
        setContexts(data.contexts);
        setStartHere(data.startHere);
      })
      .catch(() => { if (!cancelled) setLoadError(true); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSelectContext = useCallback((slug) => {
    posthog?.capture?.('discovery_context_picked', { context: slug });
    navigate(`/discover/${encodeURIComponent(slug)}`);
  }, [navigate]);

  // Start-here is the only card on this page — position 0 by construction.
  const handleSelectStartHere = useCallback((card) => {
    posthog?.capture?.('discovery_card_tapped', {
      scenario_id: card.id,
      prep_required: !!card.prep_required,
      position: 0,
    });
    setCommittingScenario(card);
  }, []);

  const handleStart = useCallback(async (modality) => {
    if (!committingScenario) return;
    setIsStarting(true);
    setStartError(null);
    posthog?.capture?.('discovery_run_started', {
      scenario_id: committingScenario.id,
      modality,
    });
    try {
      await startGuestRun({
        scenarioId: committingScenario.id,
        modality,
        isAuthenticated,
        user,
        loginAsGuest,
        safeAuthFetch,
        apiBaseUrl: API_BASE_URL,
        navigate,
      });
    } catch (err) {
      console.error('Failed to start guest session:', err);
      setStartError(guestStartErrorMessage(err, 'Something went wrong starting that conversation. Try again.', t));
      setIsStarting(false);
    }
  }, [committingScenario, isAuthenticated, user, loginAsGuest, navigate, t]);

  return (
    <div className="gd-page gd-contexts-page">
      <header className="gd-page__header">
        {/* #3756: a plain <a>, not <Link>. A client-side route to "/" renders
            the SPA's old home page; a full page load reaches the static site's
            /demo, where "Browse conversations" brought the visitor from. With
            the static site off, /demo falls back to the SPA home as before.
            A page that tags its link with ?from= gets the visitor back
            instead (discoveryOrigin.js). */}
        <a href={origin.href} className="gd-page__back">← {origin.label}</a>
      </header>

      <h1 className="gd-page__title">Choose a scenario</h1>

      {/* #2507: recovery choices come before every variable-height catalogue
          row. On the mobile vertical axis they therefore stay in the first
          viewport; the two-column grid keeps them beside one another. */}
      <div className="gd-contexts-page__recovery-actions" data-testid="gd-recovery-actions">
        <ChooserEntry onClick={() => navigate('/discover/choose')} />
        <button type="button" className="gd-create-entry" data-testid="gd-create-entry" onClick={() => setIsAuthoringOpen(true)}>
          <span>{t('scenarios.createYourOwn', 'Create Your Own')}</span>
          <span className="gd-create-entry__hint">{t('scenarios.createYourOwnHint', 'Build one for your situation')}</span>
        </button>
      </div>

      {isLoading && <p className="gd-page__status">Loading scenarios…</p>}
      {!isLoading && loadError && (
        <p className="gd-page__status gd-page__status--error">
          Couldn't load scenarios right now. <a href={origin.href}>Back to {origin.label}</a>
        </p>
      )}

      {!isLoading && !loadError && (
        <>
          {startHere && (
            <div className="gd-contexts-page__start-here">
              <StartHereSlot card={startHere} onSelect={handleSelectStartHere} />
            </div>
          )}

          <ContextShelf contexts={contexts} onSelect={handleSelectContext} />

        </>
      )}

      {startError && <p className="gd-page__status gd-page__status--error">{startError}</p>}

      <CommitmentSheetOverlay
        scenario={committingScenario}
        isStarting={isStarting}
        onDismiss={() => { setCommittingScenario(null); setStartError(null); }}
        onStart={handleStart}
      />
      <AuthoringDemoDialog isOpen={isAuthoringOpen} onClose={() => setIsAuthoringOpen(false)} />
    </div>
  );
}

export default GuestDiscoveryContextsPage;
